'use client';

import { type BrandingFontFamily, deriveBrandColors } from '@slims/shared/branding';
import { useEffect } from 'react';
import { useCerBranding } from '@/hooks/use-cer-branding';
import { useUIStore } from '@/stores/ui-store';

/**
 * Applica il branding white-label della CER di contesto a TUTTO il portale CER
 * autenticato (WB-R22), scrivendo variabili CSS su `document.documentElement`:
 * un override inline batte qualsiasi regola di `tokens.css` (`:root`,
 * `[data-surface='cer']`, `.light`). Senza branding, o col brand SLIMS di
 * default, rimuove gli override e la palette torna quella dei token.
 *
 * I colori derivati (dark/light, contrasto-safe, variante per fondo solido)
 * vengono da `@slims/shared/branding`, lo stesso modulo del mobile: i due lati
 * non divergono. Il componente non renderizza nulla.
 */

/** Le variabili che il branding può sovrascrivere. */
const VARS = [
	'--primary',
	'--color-accent',
	'--color-accent-tint',
	'--color-accent-purple',
	'--color-accent-green',
	'--font-sans',
] as const;

/** Stack CSS per le famiglie non-Inter caricate in `index.html`. */
const FONT_STACK: Record<Exclude<BrandingFontFamily, 'inter'>, string> = {
	system: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
	lora: "'Lora', Georgia, 'Times New Roman', serif",
	roboto: "'Roboto', system-ui, -apple-system, sans-serif",
	montserrat: "'Montserrat', system-ui, -apple-system, sans-serif",
};

/** true se i due colori coincidono col brand SLIMS CER: nessun override colori. */
function isSlimsPalette(primary: string, secondary: string): boolean {
	return primary.toUpperCase() === '#00E5FF' && secondary.toUpperCase() === '#7C3AED';
}

export function BrandingEffect() {
	const branding = useCerBranding();
	const theme = useUIStore((s) => s.theme);

	useEffect(() => {
		const root = document.documentElement;
		const clearAll = () => {
			for (const v of VARS) root.style.removeProperty(v);
		};

		if (!branding) {
			clearAll();
			return clearAll;
		}

		const scheme = theme === 'light' ? 'light' : 'dark';

		if (isSlimsPalette(branding.primaryColor, branding.secondaryColor)) {
			root.style.removeProperty('--primary');
			root.style.removeProperty('--color-accent');
			root.style.removeProperty('--color-accent-tint');
			root.style.removeProperty('--color-accent-purple');
			root.style.removeProperty('--color-accent-green');
		} else {
			const c = deriveBrandColors(
				{ primaryColor: branding.primaryColor, secondaryColor: branding.secondaryColor },
				scheme,
			);
			root.style.setProperty('--primary', c.primaryColor);
			root.style.setProperty('--color-accent', c.primaryColor);
			root.style.setProperty('--color-accent-tint', c.primaryColor);
			root.style.setProperty('--color-accent-purple', c.accentPurple);
			root.style.setProperty('--color-accent-green', c.accentGreen);
		}

		if (branding.fontFamily && branding.fontFamily !== 'inter') {
			root.style.setProperty('--font-sans', FONT_STACK[branding.fontFamily]);
		} else {
			root.style.removeProperty('--font-sans');
		}

		return clearAll;
	}, [branding, theme]);

	return null;
}
